import { apiEndpoints } from '@/lib/api/endpoints'
import { baseApi } from './baseQuery'
import type { ProviderDetailsResponse } from '@/types/interfaces/provider'
import type { ReferralProviderSearchResult } from '@/lib/store/api/referral/types'

/** Params for GET /api/external/providers/search (directory / sitewide zip refresh). */
export interface DirectoryProviderSearchParams {
    zip_code: string
    specialties: string[]
    priority?: 'recommended' | 'fastest' | 'highest_rated' | 'lowest_cost'
    include_pricing?: boolean
    include_sync_fallback?: boolean
    per_page?: number
}

export interface DirectoryProviderSearchResponse {
    data: ReferralProviderSearchResult[]
    has_more: boolean
    next_cursor?: string | null
    warnings?: Array<string | Record<string, unknown>>
}

export function buildDirectoryProviderSearchQueryString(params: DirectoryProviderSearchParams): string {
    const searchParams = new URLSearchParams()
    searchParams.set('zip_code', params.zip_code)
    if (params.specialties.length > 0) {
        searchParams.set('specialties', params.specialties.join(','))
    }
    searchParams.set('priority', params.priority ?? 'recommended')
    if (params.include_pricing != null) {
        searchParams.set('include_pricing', String(params.include_pricing))
    }
    if (params.include_sync_fallback != null) {
        searchParams.set('include_sync_fallback', String(params.include_sync_fallback))
    }
    if (params.per_page != null) {
        searchParams.set('per_page', String(params.per_page))
    }
    return `?${searchParams.toString()}`
}

export const providerApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getProviderDetails: builder.query<ProviderDetailsResponse, string>({
            query: (providerId) => apiEndpoints.providers.getById(providerId),
            providesTags: (_result, _error, providerId) => [{ type: 'Provider', id: providerId }],
        }),

        searchProviders: builder.query<DirectoryProviderSearchResponse, DirectoryProviderSearchParams>({
            query: (params) =>
                `${apiEndpoints.providers.search}${buildDirectoryProviderSearchQueryString(params)}`,
            providesTags: ['Provider'],
        }),
    }),
})

export const { useGetProviderDetailsQuery, useSearchProvidersQuery } = providerApi
